// =====================================================================
// Assessment Center — Instructor Panel
// In-memory data layer, shaped exactly like the D1 tables/types in
// src/shared/types/db.ts.
//
// This is a stand-in for real Worker endpoints (e.g. `/api/instructor/
// questions`, `/api/instructor/exams`) so the panel is fully usable
// today. Every function signature is what the real fetch-based client
// should end up looking like — swap the body for a `fetch(...)` call
// per function without touching any page or component. The current
// instructor's identity comes from the signed-in session (see
// src/shared/auth) rather than a hardcoded id.
//
// No schema changes were made to produce this file.
// =====================================================================
import type {
  Question,
  QuestionType,
  QuestionDifficulty,
  QuestionStatus,
  MultipleChoiceAnswer,
  EssayQuestion,
  Exam,
  ExamType,
  ExamStatus,
  ExamSection,
  ExamQuestion,
  Course,
  QuestionBank,
} from "../../../shared/types/db";
import { requireSession } from "../../../shared/auth/session";

function currentTeacherId(): string {
  return requireSession("instructor").id;
}

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const nowIso = () => new Date().toISOString();
const delay = (ms = 220) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------
// Seed data
// ---------------------------------------------------------------------
let courses: Course[] = [
  {
    id: "course_1",
    teacher_id: "teacher_1",
    title: "ATPL Air Law",
    code: "ATPL-LAW",
    description: "Airline Transport Pilot Licence — Air Law module.",
    status: "active",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "course_2",
    teacher_id: "teacher_1",
    title: "Instrument Rating — Meteorology",
    code: "IR-MET",
    description: null,
    status: "active",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

let questionBanks: QuestionBank[] = [
  {
    id: "bank_1",
    teacher_id: "teacher_1",
    course_id: "course_1",
    title: "Air Law — Core Bank",
    description: "ICAO Annexes, licensing, rules of the air.",
    category: "Air Law",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "bank_2",
    teacher_id: "teacher_1",
    course_id: "course_2",
    title: "Meteorology — Core Bank",
    description: "METAR/TAF interpretation, weather theory.",
    category: "Meteorology",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

let questions: Question[] = [
  {
    id: "q_1",
    question_bank_id: "bank_1",
    created_by: "teacher_1",
    type: "multiple_choice",
    prompt: "Under ICAO Annex 2, VFR flight is prohibited above which flight level unless authorized?",
    difficulty: "medium",
    default_points: 1,
    media_url: null,
    status: "published",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "q_2",
    question_bank_id: "bank_1",
    created_by: "teacher_1",
    type: "multiple_choice",
    prompt: "Which document must a pilot-in-command carry for an international flight?",
    difficulty: "easy",
    default_points: 1,
    media_url: null,
    status: "published",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "q_3",
    question_bank_id: "bank_1",
    created_by: "teacher_1",
    type: "essay",
    prompt:
      "Explain the difference between controlled and uncontrolled airspace, and describe how ATC separation minima change between the two.",
    difficulty: "hard",
    default_points: 10,
    media_url: null,
    status: "published",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "q_4",
    question_bank_id: "bank_2",
    created_by: "teacher_1",
    type: "multiple_choice",
    prompt: "In a METAR, what does the group 'BKN018' indicate?",
    difficulty: "medium",
    default_points: 1,
    media_url: null,
    status: "draft",
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

let mcAnswers: MultipleChoiceAnswer[] = [
  { id: "a_1", question_id: "q_1", answer_text: "FL 195", is_correct: false, order_index: 0 },
  { id: "a_2", question_id: "q_1", answer_text: "FL 200", is_correct: true, order_index: 1 },
  { id: "a_3", question_id: "q_1", answer_text: "FL 245", is_correct: false, order_index: 2 },
  { id: "a_4", question_id: "q_1", answer_text: "FL 290", is_correct: false, order_index: 3 },

  { id: "a_5", question_id: "q_2", answer_text: "Certificate of Airworthiness only", is_correct: false, order_index: 0 },
  { id: "a_6", question_id: "q_2", answer_text: "Journey Log only", is_correct: false, order_index: 1 },
  { id: "a_7", question_id: "q_2", answer_text: "Certificate of Registration and Airworthiness, and crew licences", is_correct: true, order_index: 2 },

  { id: "a_8", question_id: "q_4", answer_text: "Broken cloud, base 1,800 ft", is_correct: true, order_index: 0 },
  { id: "a_9", question_id: "q_4", answer_text: "Broken cloud, base 18,000 ft", is_correct: false, order_index: 1 },
  { id: "a_10", question_id: "q_4", answer_text: "Blowing snow, visibility 1800 m", is_correct: false, order_index: 2 },
];

let essayDetails: EssayQuestion[] = [
  {
    question_id: "q_3",
    model_answer:
      "Controlled airspace requires ATC clearance and separation is actively provided; uncontrolled airspace relies on see-and-avoid...",
    grading_rubric: "Award points for: definition accuracy, separation minima comparison, real-world example.",
    min_words: 120,
    max_words: 400,
  },
];

let exams: Exam[] = [
  {
    id: "exam_1",
    course_id: "course_1",
    created_by: "teacher_1",
    title: "Air Law — Midterm Assessment",
    description: "Covers ICAO Annexes 1, 2, and 6.",
    exam_type: "mixed",
    status: "draft",
    duration_minutes: 60,
    passing_score: 70,
    max_attempts: 1,
    start_at: null,
    end_at: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

let examSections: ExamSection[] = [
  {
    id: "sec_1",
    exam_id: "exam_1",
    title: "Multiple Choice",
    instructions: "Choose the single best answer for each question.",
    section_type: "multiple_choice",
    order_index: 0,
    time_limit_minutes: null,
  },
  {
    id: "sec_2",
    exam_id: "exam_1",
    title: "Written Response",
    instructions: "Answer in full sentences. Cite regulation numbers where relevant.",
    section_type: "essay",
    order_index: 1,
    time_limit_minutes: null,
  },
];

let examQuestions: ExamQuestion[] = [
  { id: "eq_1", exam_section_id: "sec_1", question_id: "q_1", order_index: 0, points_override: null },
  { id: "eq_2", exam_section_id: "sec_1", question_id: "q_2", order_index: 1, points_override: null },
  { id: "eq_3", exam_section_id: "sec_2", question_id: "q_3", order_index: 0, points_override: 15 },
];

// ---------------------------------------------------------------------
// Courses / Question Banks (read-only here — authored elsewhere)
// ---------------------------------------------------------------------
export async function listCourses(): Promise<Course[]> {
  await delay();
  return courses.filter((c) => c.teacher_id === currentTeacherId());
}

export async function listQuestionBanks(): Promise<QuestionBank[]> {
  await delay();
  return questionBanks.filter((b) => b.teacher_id === currentTeacherId());
}

// ---------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------
export interface QuestionWithDetail extends Question {
  answers?: MultipleChoiceAnswer[];
  essay?: EssayQuestion;
}

function hydrateQuestion(q: Question): QuestionWithDetail {
  if (q.type === "multiple_choice") {
    return {
      ...q,
      answers: mcAnswers
        .filter((a) => a.question_id === q.id)
        .sort((a, b) => a.order_index - b.order_index),
    };
  }
  return { ...q, essay: essayDetails.find((e) => e.question_id === q.id) };
}

export interface QuestionFilters {
  bankId?: string;
  type?: QuestionType;
  status?: QuestionStatus;
  search?: string;
}

export async function listQuestions(filters: QuestionFilters = {}): Promise<QuestionWithDetail[]> {
  await delay();
  return questions
    .filter((q) => q.created_by === currentTeacherId())
    .filter((q) => !filters.bankId || q.question_bank_id === filters.bankId)
    .filter((q) => !filters.type || q.type === filters.type)
    .filter((q) => !filters.status || q.status === filters.status)
    .filter(
      (q) =>
        !filters.search ||
        q.prompt.toLowerCase().includes(filters.search.toLowerCase())
    )
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map(hydrateQuestion);
}

export async function getQuestion(id: string): Promise<QuestionWithDetail | null> {
  await delay();
  const q = questions.find((q) => q.id === id);
  return q ? hydrateQuestion(q) : null;
}

export interface MultipleChoiceAnswerInput {
  answer_text: string;
  is_correct: boolean;
}

export interface QuestionInput {
  question_bank_id: string;
  type: QuestionType;
  prompt: string;
  difficulty: QuestionDifficulty;
  default_points: number;
  status: QuestionStatus;
  // multiple_choice
  answers?: MultipleChoiceAnswerInput[];
  // essay
  model_answer?: string;
  grading_rubric?: string;
  min_words?: number | null;
  max_words?: number | null;
}

export async function createQuestion(input: QuestionInput): Promise<QuestionWithDetail> {
  await delay();
  const id = uuid();
  const question: Question = {
    id,
    question_bank_id: input.question_bank_id,
    created_by: currentTeacherId(),
    type: input.type,
    prompt: input.prompt,
    difficulty: input.difficulty,
    default_points: input.default_points,
    media_url: null,
    status: input.status,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  questions = [question, ...questions];

  if (input.type === "multiple_choice") {
    (input.answers ?? []).forEach((a, i) => {
      mcAnswers.push({
        id: uuid(),
        question_id: id,
        answer_text: a.answer_text,
        is_correct: a.is_correct,
        order_index: i,
      });
    });
  } else {
    essayDetails.push({
      question_id: id,
      model_answer: input.model_answer ?? null,
      grading_rubric: input.grading_rubric ?? null,
      min_words: input.min_words ?? null,
      max_words: input.max_words ?? null,
    });
  }
  return hydrateQuestion(question);
}

export async function updateQuestion(
  id: string,
  input: QuestionInput
): Promise<QuestionWithDetail> {
  await delay();
  const idx = questions.findIndex((q) => q.id === id);
  if (idx === -1) throw new Error("Question not found");

  const updated: Question = {
    ...questions[idx],
    question_bank_id: input.question_bank_id,
    prompt: input.prompt,
    difficulty: input.difficulty,
    default_points: input.default_points,
    status: input.status,
    updated_at: nowIso(),
  };
  questions[idx] = updated;

  if (input.type === "multiple_choice") {
    mcAnswers = mcAnswers.filter((a) => a.question_id !== id);
    (input.answers ?? []).forEach((a, i) => {
      mcAnswers.push({
        id: uuid(),
        question_id: id,
        answer_text: a.answer_text,
        is_correct: a.is_correct,
        order_index: i,
      });
    });
  } else {
    essayDetails = essayDetails.filter((e) => e.question_id !== id);
    essayDetails.push({
      question_id: id,
      model_answer: input.model_answer ?? null,
      grading_rubric: input.grading_rubric ?? null,
      min_words: input.min_words ?? null,
      max_words: input.max_words ?? null,
    });
  }
  return hydrateQuestion(updated);
}

export async function deleteQuestion(id: string): Promise<void> {
  await delay();
  // Mirrors the FK: exam_questions.question_id has ON DELETE RESTRICT,
  // so a question already placed on an exam cannot be deleted from here.
  const inUse = examQuestions.some((eq) => eq.question_id === id);
  if (inUse) {
    throw new Error(
      "This question is used on an exam and can't be deleted. Remove it from the exam first."
    );
  }
  questions = questions.filter((q) => q.id !== id);
  mcAnswers = mcAnswers.filter((a) => a.question_id !== id);
  essayDetails = essayDetails.filter((e) => e.question_id !== id);
}

// ---------------------------------------------------------------------
// Exams
// ---------------------------------------------------------------------
export interface ExamSectionInput {
  id?: string; // present when editing an existing section
  title: string;
  instructions?: string;
  section_type: QuestionType;
  time_limit_minutes?: number | null;
  question_ids: string[]; // order in this array = order_index
  points_overrides?: Record<string, number | null>;
}

export interface ExamInput {
  course_id: string | null;
  title: string;
  description?: string;
  duration_minutes: number;
  passing_score?: number | null;
  max_attempts: number;
  start_at?: string | null;
  end_at?: string | null;
  sections: ExamSectionInput[];
}

export interface ExamWithDetail extends Exam {
  sections: (ExamSection & { questions: (ExamQuestion & { question: Question })[] })[];
}

function deriveExamType(sections: ExamSectionInput[]): ExamType {
  const types = new Set(sections.map((s) => s.section_type));
  if (types.size === 0) return "multiple_choice";
  if (types.size > 1) return "mixed";
  return types.has("essay") ? "essay" : "multiple_choice";
}

function hydrateExam(exam: Exam): ExamWithDetail {
  const sections = examSections
    .filter((s) => s.exam_id === exam.id)
    .sort((a, b) => a.order_index - b.order_index)
    .map((s) => ({
      ...s,
      questions: examQuestions
        .filter((eq) => eq.exam_section_id === s.id)
        .sort((a, b) => a.order_index - b.order_index)
        .map((eq) => ({ ...eq, question: questions.find((q) => q.id === eq.question_id)! })),
    }));
  return { ...exam, sections };
}

export interface ExamFilters {
  status?: ExamStatus;
  search?: string;
}

export async function listExams(filters: ExamFilters = {}): Promise<ExamWithDetail[]> {
  await delay();
  return exams
    .filter((e) => e.created_by === currentTeacherId())
    .filter((e) => !filters.status || e.status === filters.status)
    .filter(
      (e) => !filters.search || e.title.toLowerCase().includes(filters.search.toLowerCase())
    )
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
    .map(hydrateExam);
}

export async function getExam(id: string): Promise<ExamWithDetail | null> {
  await delay();
  const exam = exams.find((e) => e.id === id);
  return exam ? hydrateExam(exam) : null;
}

function writeExam(id: string | null, input: ExamInput, status: ExamStatus): ExamWithDetail {
  const examId = id ?? uuid();
  const examType = deriveExamType(input.sections);

  const base: Exam = {
    id: examId,
    course_id: input.course_id,
    created_by: currentTeacherId(),
    title: input.title,
    description: input.description ?? null,
    exam_type: examType,
    status,
    duration_minutes: input.duration_minutes,
    passing_score: input.passing_score ?? null,
    max_attempts: input.max_attempts,
    start_at: input.start_at ?? null,
    end_at: input.end_at ?? null,
    created_at: id ? exams.find((e) => e.id === id)!.created_at : nowIso(),
    updated_at: nowIso(),
  };

  if (id) {
    exams = exams.map((e) => (e.id === id ? base : e));
    examQuestions = examQuestions.filter(
      (eq) => !examSections.some((s) => s.exam_id === id && s.id === eq.exam_section_id)
    );
    examSections = examSections.filter((s) => s.exam_id !== id);
  } else {
    exams = [base, ...exams];
  }

  input.sections.forEach((s, sIdx) => {
    const sectionId = s.id ?? uuid();
    examSections.push({
      id: sectionId,
      exam_id: examId,
      title: s.title,
      instructions: s.instructions ?? null,
      section_type: s.section_type,
      order_index: sIdx,
      time_limit_minutes: s.time_limit_minutes ?? null,
    });
    s.question_ids.forEach((qId, qIdx) => {
      examQuestions.push({
        id: uuid(),
        exam_section_id: sectionId,
        question_id: qId,
        order_index: qIdx,
        points_override: s.points_overrides?.[qId] ?? null,
      });
    });
  });

  return hydrateExam(base);
}

/** Creates or updates an exam and leaves it in `draft` status. */
export async function saveExamDraft(input: ExamInput, id?: string): Promise<ExamWithDetail> {
  await delay();
  return writeExam(id ?? null, input, "draft");
}

/** Creates or updates an exam and immediately sets it to `published`. */
export async function publishExam(input: ExamInput, id?: string): Promise<ExamWithDetail> {
  await delay();
  if (input.sections.length === 0) {
    throw new Error("Add at least one section with questions before publishing.");
  }
  if (input.sections.some((s) => s.question_ids.length === 0)) {
    throw new Error("Every section needs at least one question before publishing.");
  }
  if (!input.start_at || !input.end_at) {
    throw new Error("Set a start and end date before publishing.");
  }
  return writeExam(id ?? null, input, "published");
}
